<template>
    <page :tabs="tabs" :busy="busy">
        <grid ref="grid" :columnDefs="columnDefs" :fetchCallback="fetchCallback">
            <date-range-picker
                key="guest-reviews-date-range-picker"
                :range-options="predefinedRanges"
                :value="guestReviewsDateRange"
                :date-format="userUnicodeDateFormat"
                @input="guestReviewsDateRangeChange"
            />
            <b-button-group class="mr-1">
                <search-multiselect
                    :icon="['fas', 'hotel']"
                    v-model="selectedProperties"
                    :options="properties"
                    variant="primary"
                    itemLabel="propertie"
                    placeholder="Properties"
                    controlValue="Properties"
                    @input="refresh"
                ></search-multiselect>
            </b-button-group>
            <responsive-button
                :href="exportExcelHref"
                :icon="['fas', 'file-excel']"
                @click="generatingExcelReportSuccessToast"
                variant="primary"
            />
            <responsive-button
                @click="$bvModal.show(copyReviewModalId)"
                variant="primary"
                :icon="['fas', 'copy']"
            ></responsive-button>
            <responsive-button
                @click="$bvModal.show(addReviewModalId)"
                variant="primary"
                :icon="['fas', 'plus']"
            ></responsive-button>
            <b-button-group>
                <more-filters-dropdown v-slot:default="{ handleDropdownShow, handleDropdownHide }">
                    <responsive-dropdown
                        :icon="['fas', 'filter']"
                        :items="reviewSourceFilterOptions"
                        variant="primary"
                        @show="handleDropdownShow"
                        @hide="handleDropdownHide"
                        :responsiveness="false"
                    >
                        {{ reviewSourceFilterLabel }}
                    </responsive-dropdown>
                    <responsive-dropdown
                        :icon="['fas', 'language']"
                        :items="languageFilterOptions"
                        variant="primary"
                        @show="handleDropdownShow"
                        @hide="handleDropdownHide"
                        :responsiveness="false"
                    >
                        {{ selectedLanguageLabel }}
                    </responsive-dropdown>
                    <responsive-dropdown
                        :icon="['fas', 'filter']"
                        :items="includePropertiesOptions"
                        variant="primary"
                        @show="handleDropdownShow"
                        @hide="handleDropdownHide"
                        :responsiveness="false"
                    >
                        {{ selectedPropertyFilterLabel }}
                    </responsive-dropdown>
                </more-filters-dropdown>
            </b-button-group>
            <validation-observer ref="copy_form" v-slot="{ meta, errors, validate }">
                <modal
                    :id="copyReviewModalId"
                    title="Copy Review"
                    @ok.prevent="copyReviews"
                    :ok-disabled="!reviewsForCopy.length || !meta.valid || selectedReviews.length === 0"
                    @hide="clearData"
                    @shown="validate()"
                >
                    <busy-modal :show="loading" />
                    <validation-provider name="selectedSource" rules="required">
                        <form-select
                            floatingLabel="From Property"
                            v-model="selectedSource"
                            :options="properties"
                            @change="fetchReviewFromSource"
                            :error="errors.selectedSource"
                        />
                    </validation-provider>
                    <validation-provider v-slot="{ handleChange }" name="selectedProperty" rules="required">
                        <form-select
                            floatingLabel="To Property"
                            v-model="selectedProperty"
                            :options="properties"
                            :error="errors.selectedProperty"
                            @input="handleChange"
                        />
                    </validation-provider>
                    <div v-show="reviewsForCopy.length > 0">
                        <grid
                            ref="grid_copy_reviews"
                            gridTheme="ag-theme-alpine-property-setup"
                            row-selection="multiple"
                            :suppress-row-click-selection="true"
                            :column-defs="reviewsCopyColumnDefs"
                            :header-height="50"
                            :rowData="reviewsForCopy"
                            @selection-changed="handleSelectionChange"
                            :isSearch="false"
                            :isAutoHeight="true"
                            >Reviews:</grid
                        >
                    </div>
                </modal>
            </validation-observer>
            <create-edit-review-modal
                :modalId="addReviewModalId"
                title="Add Review"
                @submit="addReview"
                :properties="properties"
                :languages="languageOptions"
            />
        </grid>
    </page>
</template>

<script>
    import { mapGetters } from 'vuex';
    import format from 'date-fns/format';
    import BusyModal from '../../generic/busy-modal/busy-modal.vue';
    import responsiveButton from '../../generic/responsive-button/responsive-button.vue';
    import Modal from '../../generic/modal/modal.vue';

    import api from '../../../api';
    import {
        GUEST_REVIEWS_ENDPOINT,
        HOSTED_FORM_ENDPOINT,
        RENTAL_PROPERTY_SETUP_REVIEWS_ENDPOINT,
        PARTNER_AIRBNB_HOST_REVIEW_RESPOND_TO_REVIEW_ENDPOINT,
    } from '../../../endpoints';
    import DateRangePicker, {
        ALL_DATES,
        LAST_MONTH,
        LAST_YEAR,
        LAST_WEEK,
        THIS_WEEK,
        nameToRange,
        THIS_MONTH,
        THIS_YEAR,
        TODAY,
        YESTERDAY,
        FUTURE,
    } from '../../generic/date-range-picker/date-range-picker.vue';
    import grid from '../../generic/grid/grid.vue';
    import Page from '../../generic/page/page.vue';
    import searchMultiselect from '../../generic/search-multiselect/search-multiselect.vue';
    import responsiveDropdown from '../../generic/responsive-dropdown/responsive-dropdown.vue';
    import { formatResponseError } from '../../../utils/api';
    import StarsCellRenderer from './stars-cell-renderer.vue';
    import ActionsCellRenderer from './actions-cell-renderer.vue';
    import FormSelect from '../../generic/form-select/form-select.vue';
    import CreateEditReviewModal from '../../property/setup/reviews/create-edit-review-modal.vue';
    import moreFiltersDropdown from '../../generic/more-filters-dropdown/more-filters-dropdown.vue';
    import { escapeHTML } from '../../../utils/strings';
    import unescape from 'lodash/unescape';

    export default {
        components: {
            grid,
            Page,
            DateRangePicker,
            searchMultiselect,
            responsiveDropdown,
            BusyModal,
            FormSelect,
            responsiveButton,
            Modal,
            CreateEditReviewModal,
            moreFiltersDropdown,
            starsCellRenderer: StarsCellRenderer,
            actionsCellRenderer: ActionsCellRenderer,
        },
        data() {
            return {
                activeLanguages: [],
                busy: false,
                loading: false,
                dateRequestFormat: 'yyyy-MM-dd',
                desc: false,
                guestReviewsDateRange: nameToRange(THIS_YEAR),
                selectedProperties: [],
                predefinedRanges: [
                    FUTURE,
                    TODAY,
                    YESTERDAY,
                    THIS_WEEK,
                    LAST_WEEK,
                    THIS_MONTH,
                    LAST_MONTH,
                    THIS_YEAR,
                    LAST_YEAR,
                    ALL_DATES,
                ],
                properties: [],
                offset: 0,
                limit: 20,
                reviewSourceFilter: 'ALL',
                reviewSourceFilterLabel: 'Show All Reviews',
                reviewsForCopy: [],
                rowData: [],
                search: '',
                selectedSource: '',
                selectedReviews: [],
                selectedProperty: '',
                selectedLanguage: null,
                selectedLanguageLabel: 'All Languages',
                selectedPropertyFilter: 'EXCLUDE',
                selectedPropertyFilterLabel: 'Exclude Disabled Properties',
                sort: 'id',
            };
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat']),
            tabs() {
                return [
                    {
                        title: 'Reviews',
                        route: '/app/guest/reviews/',
                        active: this.$route.path === '/app/guest/reviews/',
                    },
                ];
            },
            copyReviewModalId() {
                return 'copy-review-modal-id';
            },
            addReviewModalId() {
                return 'add-review-modal-id';
            },

            columnDefs() {
                return [
                    {
                        field: 'date',
                        headerName: 'Date',
                        type: 'dateColumn',
                        minWidth: 200,
                        maxWidth: 200,
                    },
                    {
                        field: 'source',
                        headerName: 'Source',
                        minWidth: 150,
                        sortable: false,
                    },
                    {
                        field: 'language',
                        headerName: 'Language',
                        cellRenderer: ({ data }) => (data.language ? data.language.name : 'English'),
                        sortable: false,
                    },
                    {
                        field: 'meta_reservation',
                        headerName: 'Invoice',
                        cellRenderer: ({ data }) => {
                            return data.meta_reservation
                                ? `<a href="/app/reservation/edit/invoice/${data.meta_reservation}">${data.meta_reservation}</a>`
                                : '-';
                        },
                    },
                    {
                        field: 'rental_property.name',
                        headerName: 'Property',
                        minWidth: 150,
                    },
                    {
                        field: 'name',
                        headerName: 'Name',
                        minWidth: 150,
                    },
                    {
                        field: 'title',
                        headerName: 'Title',
                        minWidth: 150,
                        cellRenderer: ({ data }) => {
                            return escapeHTML(unescape(data.title));
                        },
                    },
                    {
                        field: 'description',
                        headerName: 'Description',
                        minWidth: 150,
                        cellRenderer: ({ data }) => {
                            return escapeHTML(unescape(data.description));
                        },
                    },
                    {
                        field: 'stars',
                        headerName: 'Rating',
                        minWidth: 140,
                        maxWidth: 140,
                        cellRenderer: 'starsCellRenderer',
                    },
                    {
                        field: '',
                        headerName: '',
                        cellRenderer: 'actionsCellRenderer',
                        maxWidth: 200,
                        minWidth: 200,
                        sortable: false,
                        cellRendererParams: {
                            properties: this.properties,
                            languages: this.languageOptions,
                            deleteReview: async (id, modalId) => {
                                try {
                                    await api.delete(GUEST_REVIEWS_ENDPOINT(id));
                                    this.$bvModal.hide(modalId);
                                    this.refresh();
                                } catch (e) {
                                    const { generic } = formatResponseError();
                                    this.errorToast({ text: generic || 'Could not delete review.' });
                                }
                            },
                            editReview: async (values, modalId) => {
                                try {
                                    const language = this.activeLanguages.find(
                                        (item) => item.code === values.language
                                    )?.language_id;

                                    await api.patch(GUEST_REVIEWS_ENDPOINT(values.id), {
                                        ...values,
                                        language,
                                    });
                                    this.$bvModal.hide(modalId);
                                    this.refresh();
                                } catch (e) {
                                    const { generic } = formatResponseError();
                                    this.errorToast({ text: generic || 'Could not edit review.' });
                                }
                            },
                            submitResponse: async (message, id, modalId, isAirbnb) => {
                                try {
                                    if (isAirbnb) {
                                        await api.post(PARTNER_AIRBNB_HOST_REVIEW_RESPOND_TO_REVIEW_ENDPOINT(id), {
                                            response: message,
                                        });
                                    } else {
                                        await api.post(GUEST_REVIEWS_ENDPOINT(id) + 'response/', {
                                            message,
                                        });
                                    }
                                    this.$bvModal.hide(modalId);
                                    this.successToast({ text: 'Response successfully sent.' });
                                    this.refresh();
                                } catch (e) {
                                    console.log(e);
                                    const { generic } = formatResponseError(e);
                                    this.errorToast({ text: generic || 'Could not send response.' });
                                }
                            },
                            deleteResponse: async (id, modalId) => {
                                try {
                                    await api.delete(GUEST_REVIEWS_ENDPOINT(id) + 'delete-host-response/');
                                    this.$bvModal.hide(modalId);
                                    this.successToast({ text: 'Response successfully deleted.' });
                                    this.refresh();
                                } catch (e) {
                                    console.log(e);
                                    const { generic } = formatResponseError(e);
                                    this.errorToast({ text: generic || 'Could not delete response.' });
                                }
                            },
                        },
                    },
                ];
            },
            endDateRequest() {
                return format(this.guestReviewsDateRange.to, this.dateRequestFormat);
            },
            startDateRequest() {
                return format(this.guestReviewsDateRange.from, this.dateRequestFormat);
            },
            fetchCallback() {
                return ({ offset, limit, sort, desc, search }) => {
                    this.offset = offset;
                    this.limit = limit;
                    this.sort = sort;
                    this.desc = desc;
                    this.search = search;

                    return api.get(GUEST_REVIEWS_ENDPOINT() + 'grid/', {
                        params: {
                            offset,
                            limit,
                            sort,
                            desc,
                            search,
                            start_date: this.startDateRequest,
                            end_date: this.endDateRequest,
                            properties: this.selectedProperties.join(','),
                            source: this.reviewSourceFilter,
                            language: this.selectedLanguage,
                            with_disabled: this.selectedPropertyFilter,
                        },
                    });
                };
            },
            reviewSourceFilterOptions() {
                return [
                    {
                        label: 'Show All Reviews',
                        click: () => {
                            this.reviewSourceFilter = 'ALL';
                            this.reviewSourceFilterLabel = 'Show All Reviews';
                            this.refresh();
                        },
                    },
                    {
                        label: 'Show Airbnb Reviews',
                        click: () => {
                            this.reviewSourceFilter = 'AIRBNB';
                            this.reviewSourceFilterLabel = 'Show Airbnb Reviews';
                            this.refresh();
                        },
                    },
                    {
                        label: 'Show Online Reviews',
                        click: () => {
                            this.reviewSourceFilter = 'ONLINE';
                            this.reviewSourceFilterLabel = 'Show Online Reviews';
                            this.refresh();
                        },
                    },
                    {
                        label: 'Show Staylio Reviews',
                        click: () => {
                            this.reviewSourceFilter = 'STAYLIO';
                            this.reviewSourceFilterLabel = 'Show Staylio Reviews';
                            this.refresh();
                        },
                    },
                ];
            },
            languageFilterOptions() {
                return [
                    {
                        label: 'All Languages',
                        click: () => {
                            this.selectedLanguage = null;
                            this.selectedLanguageLabel = 'All Languages';
                            this.refresh();
                        },
                    },
                    ...this.activeLanguages.map((item) => {
                        return {
                            label: item.name,
                            click: () => {
                                this.selectedLanguage = item.code;
                                this.selectedLanguageLabel = item.name;
                                this.refresh();
                            },
                        };
                    }),
                ];
            },
            languageOptions() {
                return [...this.activeLanguages.map((item) => ({ value: item.code, text: item.name }))];
            },
            includePropertiesOptions() {
                return [
                    {
                        label: 'Include Disabled Properties',
                        click: () => {
                            this.selectedPropertyFilter = 'INCLUDE';
                            this.selectedPropertyFilterLabel = 'Include Disabled Properties';
                            this.fetchProperties();
                            this.refresh();
                        },
                    },
                    {
                        label: 'Exclude Disabled Properties',
                        click: () => {
                            this.selectedPropertyFilter = 'EXCLUDE';
                            this.selectedPropertyFilterLabel = 'Exclude Disabled Properties';
                            this.fetchProperties();
                            this.refresh();
                        },
                    },
                ];
            },

            reviewsCopyColumnDefs() {
                return [
                    {
                        field: 'name',
                        headerName: 'Name',
                        headerCheckboxSelection: true,
                        headerCheckboxSelectionFilteredOnly: true,
                        checkboxSelection: true,
                    },
                    {
                        field: 'stars',
                        headerName: 'Stars',
                    },
                ];
            },
            exportExcelHref() {
                let url = `${GUEST_REVIEWS_ENDPOINT()}excel/`;
                url += `?from_date=${this.startDateRequest}`;
                url += `&to_date=${this.endDateRequest}`;
                url += `&properties=${this.selectedProperties.length > 0 ? this.selectedProperties.join(',') : ''}`;
                url += `&source=${this.reviewSourceFilter}`;
                url += this.selectedLanguage ? `&language=${this.selectedLanguage}` : '';
                url += `&with_disabled=${this.selectedPropertyFilter}`;
                url += `&offset=0`;
                url += `&limit=1`;
                return url;
            },
        },
        mounted() {
            Promise.all([this.fetchProperties(), this.fetchActiveLanguages()]);
        },
        methods: {
            async addReview(values, modalId) {
                try {
                    const language = this.activeLanguages.find((item) => item.code === values.language)?.language_id;
                    await api.post(GUEST_REVIEWS_ENDPOINT(), {
                        ...values,
                        language,
                    });
                    this.$bvModal.hide(modalId);
                    this.refresh();
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not create review.' });
                }
            },
            async fetchProperties() {
                try {
                    const { data } = await api.get(GUEST_REVIEWS_ENDPOINT() + 'properties/', {
                        params: {
                            with_disabled: this.selectedPropertyFilter === 'INCLUDE' ? 1 : 0,
                        },
                    });
                    this.properties = data.map((item) => ({
                        value: item.property_id,
                        text: item.name,
                    }));
                } catch (e) {
                    this.errorToast({ text: 'Could not load properties' });
                }
            },
            async fetchActiveLanguages() {
                try {
                    const { data } = await api.get(GUEST_REVIEWS_ENDPOINT() + 'languages/');
                    this.activeLanguages = [{ id: null, code: 'en', name: 'English' }, ...data];
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch active languages' });
                } finally {
                    this.busy = false;
                }
            },
            guestReviewsDateRangeChange(range) {
                this.guestReviewsDateRange = range;
                this.refresh();
            },
            refresh() {
                this.$refs.grid.refreshDataSource();
            },
            handleSelectionChange() {
                const gridApi = this.$refs.grid_copy_reviews.gridOptions.api;
                this.selectedReviews = gridApi.getSelectedRows();
            },
            async fetchReviewFromSource(value) {
                if (value) {
                    try {
                        this.loading = true;
                        const { data } = await api.get(
                            `${RENTAL_PROPERTY_SETUP_REVIEWS_ENDPOINT(value)}reviews-source/`,
                            {
                                params: {
                                    code: null,
                                },
                            }
                        );
                        this.reviewsForCopy = data;
                        this.$refs.copy_form.setFieldValue('selectedSource', value);
                    } catch (e) {
                        const { generic } = formatResponseError(e);
                        this.errorToast({ text: generic || 'Could not fetch reviews' });
                    } finally {
                        this.loading = false;
                    }
                }
            },
            async copyReviews() {
                try {
                    this.loading = true;

                    const selectedReviews = this.selectedReviews.map((item) => item.id);
                    await api.post(`${RENTAL_PROPERTY_SETUP_REVIEWS_ENDPOINT(this.selectedProperty)}copy-reviews/`, {
                        id: this.selectedSource,
                        reviews_ids: selectedReviews,
                    });
                    this.refresh();
                    this.$bvModal.hide(this.copyReviewModalId);
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not copy review' });
                } finally {
                    this.loading = false;
                }
            },
            clearData() {
                this.selectedReviews = [];
                this.selectedSource = '';
                this.selectedProperty = '';
            },
        },
    };
</script>

<style></style>
